import { useState } from "react";
import { useApi, useTracker } from "@ferrivia/auth-web";
import { useFormatters, useMarket, useTranslation } from "@ferrivia/i18n";
import { openPdf } from "@ferrivia/lab-ui";
import { Text } from "@ferrivia/ui";
import { useCustomerLabReportFiles } from "../../../../hooks/useCustomerLabReportFiles";
import s from "./LabsPane.module.css";

/* PDFs the customer uploaded, opened exactly as uploaded. Nothing in them is
   read, extracted or compared by the software; the clinician reads the
   document. US deployment only. */
export function LabReportFiles({ customerId }: { customerId: string }) {
  const market = useMarket();
  const { t } = useTranslation("clinic");
  const { tag } = useFormatters();
  const api = useApi();
  const tracker = useTracker();
  const state = useCustomerLabReportFiles(customerId, market === "us");
  const [failed, setFailed] = useState(false);

  if (market !== "us" || state.status !== "success" || state.files.length === 0) {
    return null;
  }

  async function open(fileId: string) {
    setFailed(false);
    try {
      await openPdf(() => api.patients.labReportFileContent(customerId, fileId));
      tracker.track("clinic.lab_report_opened", { patient_id: customerId });
    } catch {
      setFailed(true);
    }
  }

  return (
    <section className={s.section} aria-labelledby="labs-reports-heading">
      <Text as="h3" size="xs" weight="medium" className={s.heading} id="labs-reports-heading">
        {t("labsPane.reports.heading", "Uploaded lab reports")}
      </Text>
      <ul className={s.reportList}>
        {state.files.map((file) => (
          <li key={file.id}>
            <button type="button" className={s.reportItem} onClick={() => void open(file.id)}>
              {t("labsPane.reports.item", "Report, sample taken {{date}}", {
                date: new Intl.DateTimeFormat(tag, {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  timeZone: "UTC",
                }).format(new Date(`${file.collectedOn}T00:00:00Z`)),
              })}
            </button>
          </li>
        ))}
      </ul>
      {failed && (
        <Text as="p" size="sm" className={s.helper}>
          {t("labsPane.reports.openFailed", "Couldn't open the report.")}
        </Text>
      )}
    </section>
  );
}
